import { useState, useRef, useEffect } from 'react'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Button } from '@/components/ui/button'
import { useTranslation } from 'react-i18next'

interface TwoFactorInputProps {
  onSubmit: (token: string) => void
  onUseBackupCode?: () => void
  isLoading?: boolean
  error?: string
}

export function TwoFactorInput({
  onSubmit,
  onUseBackupCode,
  isLoading,
  error,
}: TwoFactorInputProps) {
  const { t } = useTranslation()
  const [code, setCode] = useState(['', '', '', '', '', ''])
  const inputRefs = useRef<(HTMLInputElement | null)[]>([])

  // Auto-focus first input on mount
  useEffect(() => {
    inputRefs.current[0]?.focus()
  }, [])

  const handleChange = (index: number, value: string) => {
    // Only allow digits
    if (value && !/^\d$/.test(value)) {
      return
    }

    const newCode = [...code]
    newCode[index] = value

    setCode(newCode)

    // Auto-focus next input
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus()
    }

    // Auto-submit when all 6 digits are entered
    if (value && index === 5 && newCode.every(digit => digit !== '')) {
      onSubmit(newCode.join(''))
    }
  }

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      inputRefs.current[index - 1]?.focus()
    }
    if (e.key === 'Enter' && code.every(digit => digit !== '')) {
      onSubmit(code.join(''))
    }
  }

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault()
    const pastedData = e.clipboardData.getData('text').trim()

    // Only process if it's exactly 6 digits
    if (/^\d{6}$/.test(pastedData)) {
      const newCode = pastedData.split('')
      setCode(newCode)
      inputRefs.current[5]?.focus()
      onSubmit(pastedData)
    }
  }

  const handleManualSubmit = () => {
    if (code.every(digit => digit !== '')) {
      onSubmit(code.join(''))
    }
  }

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label className="text-center block">
          {t('auth.twoFactorCode', 'Enter 6-digit verification code')}
        </Label>
        <div className="flex gap-2 justify-center" onPaste={handlePaste}>
          {code.map((digit, index) => (
            <Input
              key={index}
              ref={el => {
                inputRefs.current[index] = el
              }}
              type="text"
              inputMode="numeric"
              maxLength={1}
              value={digit}
              onChange={e => handleChange(index, e.target.value)}
              onKeyDown={e => handleKeyDown(index, e)}
              className="w-12 h-12 text-center text-lg font-semibold"
              disabled={isLoading}
            />
          ))}
        </div>
        {error && <p className="text-sm text-destructive text-center">{error}</p>}
      </div>

      <div className="space-y-2">
        <Button
          type="button"
          onClick={handleManualSubmit}
          className="w-full"
          disabled={isLoading || !code.every(digit => digit !== '')}
        >
          {isLoading ? t('algemeen.laden', 'Loading...') : t('auth.verify', 'Verify')}
        </Button>

        {onUseBackupCode && (
          <Button
            type="button"
            variant="ghost"
            onClick={onUseBackupCode}
            className="w-full"
            disabled={isLoading}
          >
            {t('auth.useBackupCode', 'Use backup code instead')}
          </Button>
        )}
      </div>
    </div>
  )
}
